/**
 * AMAHC predictive prefetch.
 *
 * After page `load` + idle, prefetches the HTML of the most likely-clicked
 * collection URLs so subsequent navigation is near-instant.
 *
 * Targets:
 *   - Slideshow CTAs (homepage)
 *   - Top nav menu collection links (every page)
 *   - Hero/banner CTAs that link to collections
 *
 * Safety:
 *   - Bounded to MAX_URLS so we never go wild
 *   - Fires AFTER `load` so no competition with critical resources
 *   - Idle-paced via requestIdleCallback (one at a time)
 *   - Browsers throttle <link rel=prefetch> automatically on slow networks
 *   - Skips cart/checkout/account
 */
(function () {
  'use strict';
  if (window.__cubPredictivePrefetch) return;
  window.__cubPredictivePrefetch = true;

  /* Avoid running on prerendered tabs (Chrome speculation rules) */
  if (document.prerendering) return;

  var MAX_URLS = 8; /* hard cap so a bad menu doesn't trigger 50 prefetches */
  var prefetched = new Set();

  function prefetchOnce(url) {
    if (!url || prefetched.has(url)) return;
    if (prefetched.size >= MAX_URLS) return;
    prefetched.add(url);
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    document.head.appendChild(link);
  }

  function eligible(href) {
    if (!href) return false;
    try {
      var u = new URL(href, location.origin);
      if (u.origin !== location.origin) return false;
      var p = u.pathname;
      if (p.indexOf('/cart') === 0)     return false;
      if (p.indexOf('/checkout') === 0) return false;
      if (p.indexOf('/account') === 0)  return false;
      if (p.indexOf('/admin') === 0)    return false;
      if (p === location.pathname)      return false; /* skip self */
      return u.pathname + u.search;
    } catch (e) { return false; }
  }

  function collectTargets() {
    var urls = new Set();

    /* 1. Top nav menu — header menu links to collections */
    document.querySelectorAll(
      '.header a[href*="/collections/"], ' +
      '.header__menu a[href*="/collections/"], ' +
      '.header__navigation a[href*="/collections/"], ' +
      'nav a[href*="/collections/"]'
    ).forEach(function (a) {
      var u = eligible(a.href);
      if (u) urls.add(u);
    });

    /* 2. Slideshow / banner CTAs (homepage hero zone) */
    document.querySelectorAll(
      '.banner a[href*="/collections/"], ' +
      '.slideshow a[href*="/collections/"], ' +
      '.image-banner a[href*="/collections/"], ' +
      '.hero a[href*="/collections/"]'
    ).forEach(function (a) {
      var u = eligible(a.href);
      if (u) urls.add(u);
    });

    /* Cap at MAX_URLS, prioritising nav menu first (it was added first) */
    return Array.from(urls).slice(0, MAX_URLS);
  }

  function schedule() {
    var urls = collectTargets();
    if (urls.length === 0) return;

    var i = 0;
    function next() {
      if (i >= urls.length) return;
      prefetchOnce(urls[i++]);
      var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 800); };
      idle(next, { timeout: 2500 });
    }
    /* Wait so the current page's critical resources finish first */
    setTimeout(next, 2000);
  }

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
})();
